// app/src/modules/ui/WhatsApp.tsx — WhatsApp pieces used across the back office:
//   <WaBadge />      header pill: is the shop's WhatsApp linked?
//   <WaSendButton /> "WhatsApp" button that opens a compose dialog and sends
//                    from the shop's linked account (not the user's phone).
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useSession } from "../../lib/session";
import { ModalShell } from "./primitives";
import { toast } from "./Dialog";
import { SOCIALS, SocialIcon } from "./SocialIcons";

const WA = SOCIALS.find((s) => s.key === "whatsapp")!;

export interface WaBadgeState { state: string; number?: string | null; name?: string | null }

const LABEL: Record<string, { text: string; tone: "good" | "warn" | "bad" | "off" }> = {
  open: { text: "WhatsApp", tone: "good" },
  qr: { text: "WhatsApp · scan QR", tone: "warn" },
  connecting: { text: "WhatsApp · connecting", tone: "warn" },
  starting: { text: "WhatsApp · starting", tone: "warn" },
  closed: { text: "WhatsApp · reconnecting", tone: "warn" },
  logged_out: { text: "WhatsApp · not linked", tone: "bad" },
  unlinked: { text: "WhatsApp · not linked", tone: "bad" },
};

/** Polls the cheap /badge endpoint (database read; the server wakes a silent companion). */
export function useWaBadge(enabled: boolean, everyMs = 60_000): WaBadgeState | null {
  const [s, setS] = useState<WaBadgeState | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    const load = () => api.get<WaBadgeState>("/whatsapp/badge").then((r) => { if (live) setS(r); }).catch(() => {});
    load();
    const t = setInterval(load, everyMs);
    return () => { live = false; clearInterval(t); };
  }, [enabled, everyMs]);
  return s;
}

/** Header pill. Only for people who can manage settings; click opens WhatsApp setup. */
export function WaBadge() {
  const { user } = useSession();
  const can = !!user?.permissions.includes("settings.manage");
  const s = useWaBadge(can);
  const nav = useNavigate();
  if (!can || !s || s.state === "unconfigured") return null;
  const l = LABEL[s.state] ?? LABEL.unlinked;
  const title = s.state === "open" ? `WhatsApp linked${s.number ? ` — ${s.number}` : ""}${s.name ? ` (${s.name})` : ""}` : "WhatsApp isn't linked — open WhatsApp setup";
  return (
    <button type="button" className={`wa-badge is-${l.tone}`} onClick={() => nav("/whatsapp")} title={title} aria-label={title}>
      <SocialIcon def={WA} size={15} mono />
      <span className="wa-badge-dot" aria-hidden />
      <span className="wa-badge-txt">{l.text}</span>
    </button>
  );
}

/** Opens a compose dialog and sends from the shop's linked WhatsApp. The server
 *  resolves the number from the record when an id is given. */
export function WaSendButton({ to, customerId, supplierId, jobId, name, preset, label = "WhatsApp", compact = false }: {
  to?: string | null; customerId?: string; supplierId?: string; jobId?: string; name?: string | null; preset?: string; label?: string; compact?: boolean;
}) {
  const { user } = useSession();
  const [open, setOpen] = useState(false);
  if (!user?.permissions.includes("message.send")) return null;
  if (!to && !customerId && !supplierId) return null;
  // Wrapped so clicks inside the dialog don't reach a table row it sits in.
  return (
    <span onClick={(e) => e.stopPropagation()} style={{ display: "inline-flex" }}>
      <button type="button" className={`wa-send-btn${compact ? " is-compact" : ""}`} onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        aria-label={`${label}${name ? ` ${name}` : ""}`} title={`Message ${name || "them"} on WhatsApp`}>
        <SocialIcon def={WA} size={16} mono />{!compact && <span>{label}</span>}
      </button>
      {open && <WaCompose to={to ?? ""} customerId={customerId} supplierId={supplierId} jobId={jobId} name={name} preset={preset} onClose={() => setOpen(false)} />}
    </span>
  );
}

export function WaCompose({ to, customerId, supplierId, jobId, name, preset, onClose, onSent }: {
  to: string; customerId?: string; supplierId?: string; jobId?: string; name?: string | null; preset?: string; onClose: () => void; onSent?: () => void;
}) {
  const [num, setNum] = useState(to);
  const [text, setText] = useState(preset ?? (name ? `Hi ${String(name).split(" ")[0]}, ` : ""));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function send() {
    setBusy(true); setErr(null);
    try {
      await api.post("/whatsapp/send", { to: num, text, customer_id: customerId, supplier_id: supplierId, job_id: jobId });
      toast(`Sent on WhatsApp${name ? ` to ${name}` : ""}`, "success");
      onSent?.(); onClose();
    } catch (e: any) { setErr(e?.message ?? "Couldn't send"); } finally { setBusy(false); }
  }
  return (
    <ModalShell title={`WhatsApp${name ? ` · ${name}` : ""}`} onClose={onClose} width={520}>
      <div className="stack-3">
        <label style={{ display: "grid", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-2)" }}>To</span>
          <input className="f-control" value={num} onChange={(e) => setNum(e.target.value)} placeholder={customerId || supplierId ? "Their number on file" : "07… or +44…"} inputMode="tel" />
        </label>
        <label style={{ display: "grid", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-2)" }}>Message</span>
          <textarea className="f-control" value={text} onChange={(e) => setText(e.target.value)} rows={6} style={{ padding: 10, resize: "vertical" }} maxLength={4000} />
        </label>
        <div style={{ fontSize: 12, color: "var(--muted)" }}>Sends from the shop's linked WhatsApp. *Bold* and _italic_ work as in WhatsApp.</div>
        {err && <div role="alert" style={{ color: "var(--accent-text)", fontSize: 13, fontWeight: 600 }}>{err}</div>}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={busy || !text.trim()} onClick={() => void send()}>{busy ? "Sending…" : "Send on WhatsApp"}</button>
        </div>
      </div>
    </ModalShell>
  );
}
